<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { apiService } from '@/services/apiService'
import { formatDate } from '@/utils/dateFormatter'
import { isNearExpiry, daysUntilExpiry, isExpired, getInstitutionName } from '@/components/utils/functions'
interface IUsuarioData {
	id: string
	institutions: Record<string, boolean>
	name: string
	x509: {
		serialNumber: string
		serial: string
		notBefore: string
		notAfter: string
	}
	institutionsNames: string[]
}
const usuarios = ref<IUsuarioData[]>([])

function ordenPrioridad(notAfter: string): number {
	if (isExpired(notAfter)) return 0
	if (isNearExpiry(notAfter)) return 1
	return 2
}

/** Orden: 1) Vencidos, 2) Por vencer (≤90 días), 3) Vigentes. Dentro de cada grupo, por fecha de expiración (más próxima primero). */
const usuariosOrdenados = computed(() => {
	const list = [...usuarios.value]
	return list.sort((a, b) => {
		const orderA = ordenPrioridad(a.x509.notAfter)
		const orderB = ordenPrioridad(b.x509.notAfter)
		if (orderA !== orderB) return orderA - orderB
		const dateA = new Date(a.x509.notAfter).getTime()
		const dateB = new Date(b.x509.notAfter).getTime()
		return orderA === 0 ? dateB - dateA : dateA - dateB
	})
})

const obtenerUsuarios = async (): Promise<void> => {
	try {
		const response = await apiService.get('getUsersBan')
		usuarios.value = response.data.usuarios
	} catch (err: any) {
		console.error('Error al obtener usuarios a desactivar:', err)
	}
}
const institutionsNames = async (usuario: IUsuarioData): Promise<string> => {
	const names = await Promise.all(Object.keys(usuario.institutions).map(async institutionId => await getInstitutionName(institutionId)))
	return names.join(' | ')
}
// Cargar transacciones cuando el componente se monte
onMounted(async () => {
	await obtenerUsuarios()

	// Calculamos todos los nombres en paralelo para evitar renderizar Promises.
	const updated = await Promise.all(
		usuarios.value.map(async usuario => ({
			...usuario,
			institutionsNames: await institutionsNames(usuario as IUsuarioData).then(names => names.split(' | '))
		}))
	)

	usuarios.value = updated
})
</script>
<template>
	<va-card class="bitacora-card">
		<va-card-title class="header-gradient">
			<div class="header-content">
				<div class="header-main">
					<div class="header-icon-wrapper">
						<va-icon name="calendar_month" class="header-icon" />
					</div>
					<div class="header-text">
						<h2 class="header-title">Expiración de certificados de usuarios</h2>
					</div>
				</div>
			</div>
		</va-card-title>

		<va-card-content class="card-content">
			<div class="section-title" style="margin-top: 1rem">
				<div class="section-title-content">
					<div class="row">
						<div class="col-md-10">
							<va-icon name="description" class="title-icon" />
							<span style="margin-left: 0.5rem">Usuarios</span>
						</div>
					</div>
				</div>
			</div>
			<div class="collapse-body">
				<div class="collapse-content">
					<div class="pagos-container">
						<div v-if="!usuarios.length" class="usuarios-empty">
							<va-icon name="people_outline" size="3rem" class="usuarios-empty-icon" />
							<p class="usuarios-empty-text">No hay usuarios para mostrar</p>
						</div>
						<div v-else class="usuarios-list">
							<div class="usuarios-list-header">
								<span class="col-name">Usuario</span>
								<span class="col-id">ID</span>
								<span class="col-name">Institución</span>
								<span class="col-serial">Serial</span>
								<span class="col-curp">CURP</span>
								<span class="col-date">Inicio</span>
								<span class="col-date">Expiración</span>
								<span class="col-estado">Estado</span>
							</div>
							<div v-for="usuario in usuariosOrdenados" :key="usuario.id" class="usuario-list-item">
								<div class="col-name">
									<div class="usuario-list-avatar">
										<va-icon name="person" size="1.25rem" />
									</div>
									<span class="usuario-list-name">{{ (usuario.name || 'Sin nombre').toUpperCase() }}</span>
								</div>
								<div class="col-id certificado-mono">{{ usuario.id }}</div>
								<div class="col-serial certificado-mono">{{ usuario.x509.serial }}</div>
								<div>
									<ul style="list-style-type: disc; padding-left: 1rem">
										<li style="font-size: 12px" v-for="name in usuario.institutionsNames" :key="name">{{ name }}</li>
									</ul>
								</div>
								<div class="col-curp certificado-mono">{{ usuario.x509.serialNumber }}</div>
								<div class="col-date col-date-inicio">{{ formatDate(usuario.x509.notBefore) }}</div>
								<div class="col-date col-date-expira">{{ formatDate(usuario.x509.notAfter) }}</div>
								<div class="col-estado">
									<div class="certificado-estado-wrapper certificado-estado-inline">
										<div class="certificado-estado-badge" :class="isExpired(usuario.x509.notAfter) ? 'estado-danger' : isNearExpiry(usuario.x509.notAfter) ? 'estado-warning' : 'estado-success'">
											<va-icon :name="isExpired(usuario.x509.notAfter) ? 'dangerous' : isNearExpiry(usuario.x509.notAfter) ? 'warning' : 'check_circle'" size="1rem" class="estado-icon" />
											<span class="estado-texto">
												<template v-if="isExpired(usuario.x509.notAfter)"> Venció hace {{ Math.abs(daysUntilExpiry(usuario.x509.notAfter)) }} días </template>
												<template v-else> Vence en {{ daysUntilExpiry(usuario.x509.notAfter) }} días </template>
											</span>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</va-card-content>
	</va-card>
</template>
<style scoped>
:deep(.dp__input) {
	background: var(--va-background-element);
	border: 1px solid var(--va-background-border);
	border-radius: 50px;
}

:deep(.dp__input)::placeholder {
	color: rgba(255, 255, 255, 0.7);
}
/* Header mejorado */
.header-gradient {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 0.5rem;
	border-radius: 0;
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
}

.header-gradient::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.header-icon-wrapper {
	width: 34px;
	height: 34px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.header-icon-wrapper:hover {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.header-icon {
	color: white;
	font-size: 2rem;
}

.header-text {
	flex: 1;
}

.header-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: white;
	margin: 0;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -0.5px;
}

:deep(.scrollable-table .va-data-table) {
	width: 100%;
	height: auto;
}

:deep(.scrollable-table .va-data-table__table-wrapper) {
	overflow: visible !important;
	height: auto !important;
}

:deep(.scrollable-table table) {
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table) {
	background: transparent;
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table thead) {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	position: sticky;
	top: 0;
	z-index: 10;
}

:deep(.scrollable-table .va-data-table__table thead th) {
	padding: 1rem 1.25rem;
	font-weight: 700;
	color: #1a202c;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid #e2e8f0;
}

:deep(.scrollable-table .va-data-table__table tbody tr) {
	transition: all 0.2s ease;
	border-bottom: 1px solid #f1f5f9;
}

:deep(.scrollable-table .va-data-table__table tbody tr:hover) {
	background: linear-gradient(90deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.02) 100%);
	transform: translateX(2px);
	box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
}

:deep(.scrollable-table .va-data-table__table tbody td) {
	padding: 1.25rem 1.25rem;
	color: #475569;
	font-size: 0.9375rem;
	vertical-align: middle;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@media (max-width: 768px) {
	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	:deep(.scrollable-table .va-data-table__table thead th),
	:deep(.scrollable-table .va-data-table__table tbody td) {
		padding: 0.75rem 0.5rem;
		font-size: 0.8125rem;
	}
}

@media (max-width: 480px) {
	.header-gradient {
		padding: 1.25rem;
	}
}

.bitacora-card {
	border-radius: 20px;
	padding: 1rem;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	overflow: hidden;
	background: white;
	border: none;
	width: 100%;
	display: flex;
	flex-direction: column;
}

.card-content {
	padding: 2.5rem;
	padding-bottom: 3rem;
	background: #fafbfc;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
}

/* Scrollbar personalizado para el contenido */
.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}
}

/* Asegurar que la card dentro de VaTabs ocupe todo el ancho */
:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

/* Secciones */
.section-title {
	margin-bottom: 1rem;
	padding-bottom: 1rem;
	border-bottom: 3px solid transparent;
	border-image: linear-gradient(135deg, #062342 0%, #062342 100%);
	animation: slideInLeft 0.5s ease-out;
}

.section-title-content {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	font-size: 1.5rem;
	font-weight: 700;
	color: #1a202c;
}

.title-icon {
	color: #062342;
	font-size: 1.75rem;
	animation: pulse 2s ease-in-out infinite;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	.section-title-content {
		font-size: 1.25rem;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}
}

.collapse-body {
	border: 2px solid var(--va-background-border);
	border-top: none;
	border-radius: 0 0 12px 12px;
	background: var(--va-background-element);
}

.collapse-content {
	padding: 1.25rem;
	color: var(--va-text-secondary, #4a5568);
	line-height: 1.6;
}
.pagos-container {
	min-height: 200px;
}

.pagos-container .usuarios-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 1rem;
	padding: 3rem 1.5rem;
	color: var(--va-secondary);
}

.pagos-container .usuarios-empty-icon {
	opacity: 0.6;
}

.pagos-container .usuarios-empty-text {
	margin: 0;
	font-size: 1rem;
}

.usuarios-list {
	display: flex;
	flex-direction: column;
	gap: 0;
	border: 1px solid var(--va-background-border);
	border-radius: 10px;
	overflow: hidden;
	background: var(--va-background-element);
}

.usuarios-list-header {
	display: grid;
	grid-template-columns: minmax(140px, 1.4fr) minmax(100px, 1fr) minmax(220px, 1.3fr) minmax(120px, 1fr) minmax(90px, 1fr) minmax(90px, 1fr) minmax(90px, 1fr) minmax(220px, 1.5fr);
	gap: 0.75rem 1rem;
	align-items: center;
	padding: 0.75rem 1rem;
	background: linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(30, 58, 138, 0.06) 100%);
	font-size: 0.7rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: var(--va-secondary);
	border-bottom: 2px solid rgba(37, 99, 235, 0.2);
}

.usuario-list-item {
	display: grid;
	grid-template-columns: minmax(140px, 1.4fr) minmax(100px, 1fr) minmax(220px, 1.3fr) minmax(120px, 1fr) minmax(90px, 1fr) minmax(90px, 1fr) minmax(90px, 1fr) minmax(220px, 1.5fr);
	gap: 0.75rem 1rem;
	align-items: center;
	padding: 0.75rem 1rem;
	border-bottom: 1px solid var(--va-background-border);
	transition: background 0.15s ease;
}

.usuario-list-item:last-child {
	border-bottom: none;
}

.usuario-list-item:hover {
	background: rgba(37, 99, 235, 0.04);
}

.usuario-list-item .col-name {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	min-width: 0;
}

.usuario-list-avatar {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: var(--va-primary);
	color: white;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
}

.usuario-list-name {
	font-weight: 600;
	font-size: 0.8125rem;
	color: var(--va-on-background);
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.usuario-list-item .col-id,
.usuario-list-item .col-serial,
.usuario-list-item .col-curp,
.usuario-list-item .col-institucion {
	font-size: 0.8125rem;
	word-break: break-all;
	overflow-wrap: break-word;
	white-space: normal;
	min-width: 0;
}

.usuario-list-item .col-date {
	font-size: 0.8125rem;
	color: var(--va-on-background);
}

.usuario-list-item .col-estado {
	min-width: 0;
}

.certificado-estado-inline {
	margin-top: 0;
	padding-top: 0;
	border-top: none;
	flex-wrap: wrap;
}

@media (max-width: 1200px) {
	.usuarios-list-header,
	.usuario-list-item {
		/* Se oculta col-curp => 7 columnas visibles */
		grid-template-columns: minmax(120px, 1fr) minmax(80px, 0.8fr) minmax(180px, 1fr) minmax(85px, 0.9fr) minmax(85px, 0.9fr) minmax(85px, 0.9fr) minmax(180px, 1.2fr);
	}

	.usuarios-list-header .col-curp,
	.usuario-list-item .col-curp {
		display: none;
	}
}

@media (max-width: 768px) {
	.usuarios-list-header {
		display: none;
	}

	.usuario-list-item {
		grid-template-columns: 1fr;
		grid-template-areas:
			'name'
			'meta'
			'estado';
		gap: 0.5rem;
		padding: 1rem;
	}

	.usuario-list-item .col-name {
		grid-area: name;
	}

	.usuario-list-item .col-id,
	.usuario-list-item .col-serial,
	.usuario-list-item .col-curp,
	.usuario-list-item .col-date {
		grid-area: meta;
		display: flex !important;
		flex-wrap: wrap;
		gap: 0.5rem 1rem;
		font-size: 0.75rem;
	}

	.usuario-list-item .col-id::before {
		content: 'ID: ';
		font-weight: 600;
		color: var(--va-secondary);
	}
	.usuario-list-item .col-serial::before {
		content: 'Serial: ';
		font-weight: 600;
		color: var(--va-secondary);
	}
	.usuario-list-item .col-curp::before {
		content: 'CURP: ';
		font-weight: 600;
		color: var(--va-secondary);
	}
	.usuario-list-item .col-date-inicio::before {
		content: 'Inicio: ';
		font-weight: 600;
		color: var(--va-secondary);
	}
	.usuario-list-item .col-date-expira::before {
		content: 'Expira: ';
		font-weight: 600;
		color: var(--va-secondary);
	}

	.usuario-list-item .col-estado {
		grid-area: estado;
	}
}

.certificado-mono {
	font-family: ui-monospace, 'Cascadia Code', 'Source Code Pro', Menlo, monospace;
	font-size: 0.8125rem;
}

/* Estado de vigencia: badges llamativos */
.certificado-estado-wrapper {
	display: flex;
	flex-wrap: wrap;
	gap: 0.2rem;
	margin-top: 0.5rem;
	padding-top: 0.75rem;
	border-top: 1px dashed rgba(37, 99, 235, 0.2);
}

.certificado-estado-badge {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	padding: 0.5rem 0.85rem;
	border-radius: 999px;
	font-size: 0.8125rem;
	font-weight: 700;
	letter-spacing: 0.02em;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
	transition:
		transform 0.2s ease,
		box-shadow 0.2s ease;
}

.certificado-estado-badge:hover {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.estado-icon {
	flex-shrink: 0;
	opacity: 0.95;
}

.estado-texto {
	line-height: 1.2;
}

.estado-danger {
	background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
	color: #fff;
	border: 1px solid rgba(255, 255, 255, 0.2);
}

.estado-warning {
	background: linear-gradient(135deg, #f3b710 0%, #f8e917 100%);
	color: #000;
	border: 1px solid rgba(255, 255, 255, 0.25);
}

.estado-success {
	background: linear-gradient(135deg, #15803d 0%, #166534 100%);
	color: #fff;
	border: 1px solid rgba(255, 255, 255, 0.2);
}
</style>
